import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FaMoon, FaSun } from "react-icons/fa";
import logo from "../../images/logo.png";

const THEME_KEY = "gub_theme";

const Navbar = () => {
  const [isIupcOpen, setIsIupcOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [studentId, setStudentId] = useState(null);
  const [isDark, setIsDark] = useState(false);
  const dropdownRef = useRef(null);

  const navLinks = useMemo(
    () => [
      { label: "Home", path: "/" },
      { label: "Events", path: "/events" },
      { label: "Mentors", path: "/mentors" },
      { label: "Committee", path: "/committee" },
      // { label: "Join Committee", path: "/join-committee" },
      { label: "Hall Of Shame", path: "/hallOfShame" },
      { label: "CV Maker", path: "/resume-maker" },
      { label: "QR Generator", path: "/qr-generator" },
      { label: "Contact", path: "/contact" },
    ],
    []
  );

  useEffect(() => {
    const studentLoc = JSON.parse(localStorage.getItem("gubcpa_student"));
    if (studentLoc) {
      setIsLoggedIn(true);
      setStudentId(studentLoc.user.id);
    }
  }, []);

  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_KEY);
    const shouldUseDark = savedTheme
      ? savedTheme === "dark"
      : window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;

    document.documentElement.classList.toggle("theme-dark", shouldUseDark);
    setIsDark(shouldUseDark);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsIupcOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("gubcpa_student");
    setIsLoggedIn(false);
    setStudentId(null);
    window.location.href = "/";
  };

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    document.documentElement.classList.toggle("theme-dark", next);
    localStorage.setItem(THEME_KEY, next ? "dark" : "light");
  };

  return (
    <header
      className="fixed top-0 z-50 w-full border-b"
      style={{
        background: "color-mix(in srgb, var(--surface-strong) 85%, transparent)",
        borderColor: "var(--border)",
        backdropFilter: "blur(10px)",
      }}
    >
      <nav className="mx-auto flex h-16 max-w-[86rem] items-center justify-between px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-3" onClick={() => setIsMobileMenuOpen(false)}>
          <img src={logo} alt="GUB CPC" className="h-9 w-auto" />
          <span className="hidden text-lg font-semibold sm:block">GUBCPC</span>
        </Link>

        <div className="hidden items-center gap-4 lg:flex">
          {navLinks.map((item) => (
            <Link key={item.path} to={item.path} className="rounded-md px-2.5 py-2 text-sm font-medium hover:opacity-70">
              {item.label}
            </Link>
          ))}

          <div className="relative" ref={dropdownRef}>
            <button onClick={() => setIsIupcOpen((prev) => !prev)} className="rounded-md px-2.5 py-2 text-sm font-medium hover:opacity-70">
              IUPC/ICPC
            </button>
            {isIupcOpen && (
              <div className="absolute right-0 top-10 min-w-40 rounded-xl border p-2" style={{ background: "var(--surface-strong)", borderColor: "var(--border)" }}>
                <Link to="/iupc/upcoming" className="block rounded-md px-3 py-2 text-sm hover:opacity-70" onClick={() => setIsIupcOpen(false)}>
                  Upcoming
                </Link>
                <Link to="/iupc/past" className="block rounded-md px-3 py-2 text-sm hover:opacity-70" onClick={() => setIsIupcOpen(false)}>
                  Past
                </Link>
              </div>
            )}
          </div>

          <button onClick={toggleTheme} className="home-pill flex items-center gap-2 px-3 py-2 text-xs font-semibold" aria-label="Toggle dark mode">
            {isDark ? <FaSun /> : <FaMoon />} {isDark ? "Light" : "Dark"}
          </button>

          {isLoggedIn && (
            <>
              <Link to="/overview" className="home-pill px-3 py-2 text-sm font-medium">
                {studentId}
              </Link>
              <button onClick={handleLogout} className="home-btn-secondary px-3 py-2 text-sm font-medium">
                Logout
              </button>
            </>
          )}
        </div>

        <button className="lg:hidden" onClick={() => setIsMobileMenuOpen((prev) => !prev)} aria-label="Toggle menu">
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16m-7 6h7"} />
          </svg>
        </button>
      </nav>

      {isMobileMenuOpen && (
        <div className="border-t px-4 py-3 lg:hidden" style={{ borderColor: "var(--border)", background: "var(--surface-strong)" }}>
          <div className="mb-3 flex items-center justify-between">
            <button onClick={toggleTheme} className="home-pill flex items-center gap-2 px-3 py-2 text-xs font-semibold" aria-label="Toggle dark mode">
              {isDark ? <FaSun /> : <FaMoon />} {isDark ? "Light" : "Dark"}
            </button>
          </div>

          <div className="space-y-2">
            {navLinks.map((item) => (
              <Link key={item.path} to={item.path} className="block rounded px-2 py-2 text-sm font-medium hover:opacity-70" onClick={() => setIsMobileMenuOpen(false)}>
                {item.label}
              </Link>
            ))}

            <div className="rounded border p-2" style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
              <div className="mb-1 text-xs font-semibold home-muted">IUPC/ICPC</div>
              <Link to="/iupc/upcoming" className="block rounded px-2 py-1.5 text-sm hover:opacity-70" onClick={() => setIsMobileMenuOpen(false)}>
                Upcoming
              </Link>
              <Link to="/iupc/past" className="block rounded px-2 py-1.5 text-sm hover:opacity-70" onClick={() => setIsMobileMenuOpen(false)}>
                Past
              </Link>
            </div>

            {isLoggedIn && (
              <>
                <Link to="/overview" className="block rounded border px-2 py-2 text-sm" style={{ borderColor: "var(--border)" }} onClick={() => setIsMobileMenuOpen(false)}>
                  {studentId}
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMobileMenuOpen(false);
                  }}
                  className="w-full rounded border px-2 py-2 text-left text-sm"
                  style={{ borderColor: "var(--border)" }}
                >
                  Logout
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
